import InputQuantity from '@/components/InputQuantity/InputQuantity';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import IconAdd from '@/icons/Add.svg';
import {
  ArticleConditionForm,
  ArticleConditionFormType,
  useUpdateArticleCondition,
} from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';
import useViewPort from '@/utils/useViewport';

import { ConditionFormQuestion, useQuestionErrors } from './ConditionQuestion';

export const ConditionQuestionMultiQuantifiableSelect = ({
  question,
  parentQuestion,
  type,
  editableArticleConditionForm,
}: {
  question: Extract<ConditionFormQuestion, { type: 'multi-quantifiable-select' }>;
  parentQuestion?: ConditionFormQuestion;
  type: ArticleConditionFormType;
  editableArticleConditionForm?: ArticleConditionForm;
}) => {
  const { isMobile } = useViewPort();
  const { _db, _compare } = useDatabaseTranslation();
  const { article, view, initialConditionFormData, qualityControlFormData } = useArticleContext();

  const conditionForm =
    type === 'initial-condition'
      ? initialConditionFormData?.conditionForm
      : qualityControlFormData?.conditionForm;

  const errors = useQuestionErrors({ question, parentQuestion, type });
  const { mutateAsync: updateArticleCondition } = useUpdateArticleCondition({
    requestId: article.requestId,
    articleId: article.id,
    conditionForm,
  });

  const values =
    editableArticleConditionForm?.articleConditions
      .filter((articleCondition) => articleCondition.questionId === question.id)
      .map(({ answerId, answerData }) => ({
        id: answerId,
        quantity: answerData?.quantity ?? 1,
        key: crypto.randomUUID(),
      })) ?? [];

  const hasNotAddedAllThePossibleAnswers = values.length < (question.answers?.length ?? 0);
  const lastSelectIsEmpty = !values.at(-1)?.id;
  const availableOptions =
    question.answers?.filter((option) => !values.some((v) => v.id === option.id)) ?? [];
  const config = question.config;

  const handleChange = (newValue: { id?: string | null; quantity: number; key: string }[]) => {
    const answers = newValue.map((value) => ({
      id: value.id,
      data: {
        quantity: value.quantity,
      },
    }));

    updateArticleCondition({
      questionId: question.id,
      answers,
    });
  };

  const editable =
    !!editableArticleConditionForm &&
    (type === 'initial-condition' ? view.initialCondition.editable : view.qualityControl.editable);

  if (!editable) {
    return null;
  }

  return (
    <Stack gap="0.5rem">
      {values.map((value, index) => {
        const selectedOption = question.answers?.find((option) => option.id === value.id);
        const options = [...availableOptions, ...(selectedOption ? [selectedOption] : [])].sort(
          (a, b) => _compare(a.text, b.text)
        );

        return (
          <Stack
            key={value.key}
            row
            gap={isMobile ? '0.5rem' : '1rem'}
            ariaLabel={_db(question.title)}
          >
            <InputSelect
              style={{ flex: 1 }}
              options={options}
              value={selectedOption}
              placeholder={_db(config.select.placeholder)}
              getOptionLabel={(option) => _db(option.text)}
              getOptionValue={(option) => option.id}
              onChange={(newValue) => {
                if (!newValue) {
                  return;
                }

                handleChange(
                  values.map((v) => (v.key === value.key ? { ...v, id: newValue.id } : v))
                );
              }}
              isInvalid={errors?.answers?.[index]?.missingSelect}
            />
            <InputQuantity
              quantity={value.quantity}
              size="medium"
              disableAdd={!value.id}
              disableRemove={values.length === 1 && value.quantity === 1}
              onIncrement={() => {
                handleChange(
                  values.map((v) =>
                    v.key === value.key ? { ...v, quantity: value.quantity + 1 } : v
                  )
                );
              }}
              onDecrement={() => {
                if (value.quantity === 1) {
                  handleChange(values.filter((v) => v.key !== value.key));
                } else {
                  handleChange(
                    values.map((v) =>
                      v.key === value.key ? { ...v, quantity: value.quantity - 1 } : v
                    )
                  );
                }
              }}
            />
          </Stack>
        );
      })}
      {hasNotAddedAllThePossibleAnswers && (
        <div>
          <Button
            variant="link-subtle"
            size="small"
            onPress={() => {
              handleChange([...values, { quantity: 1, key: crypto.randomUUID() }]);
            }}
            disabled={lastSelectIsEmpty}
          >
            <IconAdd />
            {_db(config.addButton.label)}
          </Button>
        </div>
      )}
    </Stack>
  );
};
